import { EvaluationReportData, V2AssessmentWithScoring } from "@/store/admin/adminTypes";
import { TRAIT_CLASS_RANGES, Trait } from "@/lib/consts";
import { getMatchScore } from "@/lib/scoring";
import { getCompetencyGroups, CompetencyGroup, CompetencyStatus } from "@/lib/competencies";

export type MatchLevel = "high" | "moderate" | "low";

// Who a participant is in the search: only candidates are ranked and tagged with a
// match level; managers and team members are shown for context (grid + spread) only.
export type ParticipantType = "candidate" | "manager" | "team";

export const PARTICIPANT_TYPE_LABELS: Record<ParticipantType, string> = {
  candidate: "Candidate",
  manager: "Manager",
  team: "Team member",
};

// Distinct per-participant identity colors, assigned by report order. The first three are
// the brand defaults for participants (ClickUp 868kwkuw2); the rest extend the palette for
// larger pools. No red — it reads as a warning next to the Limited-match pill. The S-3
// benchmark colour is not here: it comes from the benchmark document.
export const CANDIDATE_PALETTE = ["#4E6F02", "#5D52D0", "#B52C76", "#378ADD", "#EF9F27", "#1D9E75", "#7F77DD", "#0C447C", "#D4537E", "#185FA5"];

export const MATCH_TAG_LABELS: Record<MatchLevel, string> = {
  high: "Strong match",
  moderate: "Good match",
  low: "Limited match",
};

// Solid-circle colors for the three prioritization levels, matching the participant report (ElraReport8) legend.
export const STATUS_COLORS: Record<CompetencyStatus, string> = {
  "highly-prioritized": "#9BCC50",
  prioritized: "#4564C4",
  neutral: "#A1A7BB",
};

export const STATUS_LABELS: Record<CompetencyStatus, string> = {
  "highly-prioritized": "Highly prioritized",
  prioritized: "Prioritized",
  neutral: "Less prioritized",
};

// Literal keys for the discussion-flag tooltip, by the trait's priority level (2 = must-have, 0 = neutral).
export const FLAG_TOOLTIP_KEYS: Record<number, string> = {
  2: "Flag::Tooltip::MustHave",
  0: "Flag::Tooltip::Neutral",
};

// Membership in `managers` / `team` decides the type; anything else is a candidate.
// (`reportData.candidates` is not used — older reports leave it empty.)
export function getParticipantType(assessmentId: string, reportData: EvaluationReportData): ParticipantType {
  if (reportData.managers?.includes(assessmentId)) return "manager";
  if (reportData.team?.includes(assessmentId)) return "team";
  return "candidate";
}

export function getMatchLevel(score: number): MatchLevel {
  if (score >= 75) return "high";
  if (score >= 50) return "moderate";
  return "low";
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (first + last).toUpperCase();
}

export function getShortName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name.trim();
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

export interface TraitFlag {
  trait: Trait;
  level: 0 | 2;
}

export interface CandidateSummary {
  id: string;
  name: string;
  initials: string;
  shortName: string;
  color: string;
  participantType: ParticipantType;
  isCandidate: boolean;
  role: string;
  readiness: string;
  matchScore: number;
  matchLevel: MatchLevel;
  matchLabel: string;
  groups: CompetencyGroup[]; // [must-have, nice-to-have, neutral]
  traitScores: Record<string, number>;
  traitInfo: Record<string, { status: CompetencyStatus; watchOut: boolean }>;
  watchOuts: TraitFlag[];
  assessment: V2AssessmentWithScoring;
}

// Single source of the candidate match score (0–100) — used for both ranking and
// the Strong/Good/Limited match tags, so the two can never drift apart.
export function getCandidateMatchScore(assessment: V2AssessmentWithScoring, traitPriorities: { [trait: string]: 0 | 1 | 2 }, traitWeights: { [trait: string]: number }): number {
  const traitScores = assessment.scoring.trait_scores as Record<string, number>;
  const mustHaves = Object.keys(traitScores).filter((t) => traitPriorities[t] === 2 && traitScores[t] >= TRAIT_CLASS_RANGES[t as Trait].low).length;
  const niceToHaves = Object.keys(traitScores).filter((t) => traitPriorities[t] === 1 && traitScores[t] >= TRAIT_CLASS_RANGES[t as Trait].low).length;
  return getMatchScore(mustHaves, niceToHaves, traitScores, traitPriorities, traitWeights);
}

// Report display order: managers first, then team members, then candidates by match
// score (highest first). Managers/team are pinned ahead of the candidate ranking so
// their position never depends on fit to role.
export function sortAssessments(reportData: EvaluationReportData): V2AssessmentWithScoring[] {
  const assessments = reportData.assessments ?? [];
  const priorities = reportData.traitPriorities ?? {};
  const weights = reportData.traitWeights ?? {};
  const typeRank: Record<ParticipantType, number> = { manager: 0, team: 1, candidate: 2 };
  const scores = new Map(assessments.map((a) => [a.identifier, getCandidateMatchScore(a, priorities, weights)]));
  return [...assessments].sort((a, b) => {
    const byType = typeRank[getParticipantType(a.identifier, reportData)] - typeRank[getParticipantType(b.identifier, reportData)];
    return byType !== 0 ? byType : (scores.get(b.identifier) ?? 0) - (scores.get(a.identifier) ?? 0);
  });
}

// Builds the per-participant view model shared by all four tabs. `sortedAssessments`
// must already be in report order (see sortAssessments — the palette is assigned by index).
// Tabs that only deal with candidates filter on `isCandidate`.
export function buildCandidateSummaries(sortedAssessments: V2AssessmentWithScoring[], reportData: EvaluationReportData): CandidateSummary[] {
  const priorities = reportData.traitPriorities ?? {};
  const weights = reportData.traitWeights ?? {};

  return sortedAssessments.map((assessment, i) => {
    const traitScores = assessment.scoring.trait_scores as Record<string, number>;
    const groups = getCompetencyGroups(priorities, traitScores);
    const matchScore = getCandidateMatchScore(assessment, priorities, weights);
    const matchLevel = getMatchLevel(matchScore);
    const participantType = getParticipantType(assessment.identifier, reportData);

    const traitInfo: Record<string, { status: CompetencyStatus; watchOut: boolean }> = {};
    const watchOuts: TraitFlag[] = [];
    groups.forEach((group) => {
      group.items.forEach((item) => {
        traitInfo[item.trait] = { status: item.status, watchOut: item.watchOut };
        if (item.watchOut) {
          watchOuts.push({ trait: item.trait as Trait, level: group.level as 0 | 2 });
        }
      });
    });

    return {
      id: assessment.identifier,
      name: assessment.candidate.name,
      initials: getInitials(assessment.candidate.name),
      shortName: getShortName(assessment.candidate.name),
      color: CANDIDATE_PALETTE[i % CANDIDATE_PALETTE.length],
      participantType,
      isCandidate: participantType === "candidate",
      role: reportData.candidateRoles?.[assessment.identifier] ?? "",
      readiness: (assessment.scoring.readiness as string) ?? "",
      matchScore,
      matchLevel,
      matchLabel: MATCH_TAG_LABELS[matchLevel],
      groups,
      traitScores,
      traitInfo,
      watchOuts,
      assessment,
    };
  });
}

// Synthesizes the `{trait}_status` record that getOrientationText() expects
// from bellcurve trait scores, using the same cutoffs as getCompetencyGroups.
export function buildStatusScoring(traitScores: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  (Object.keys(TRAIT_CLASS_RANGES) as Trait[]).forEach((trait) => {
    const range = TRAIT_CLASS_RANGES[trait];
    const score = traitScores[trait] ?? 0;
    out[`${trait}_status`] = score >= range.high ? 2 : score >= range.low ? 1 : 0;
  });
  return out;
}

export interface TraitGroupDef {
  key: "must" | "nice" | "neutral";
  labelKey: string; // literal key — the group names are business-managed (Priority::*)
  level: 0 | 1 | 2;
  traits: string[];
}

// The three trait groups (must/nice/neutral) with their trait keys, in report order.
export function buildTraitGroups(traitPriorities: { [trait: string]: 0 | 1 | 2 }): TraitGroupDef[] {
  const defs: Array<{ key: TraitGroupDef["key"]; labelKey: string; level: 0 | 1 | 2 }> = [
    { key: "must", labelKey: "Priority::MustHaves", level: 2 },
    { key: "nice", labelKey: "Priority::NiceToHaves", level: 1 },
    { key: "neutral", labelKey: "Priority::Neutrals", level: 0 },
  ];
  return defs.map((d) => ({ ...d, traits: Object.keys(traitPriorities ?? {}).filter((t) => traitPriorities[t] === d.level) }));
}
